import { Locale } from '../locale';
import { enforceNumber } from '../typing';

export interface Month {
  id: number;
  name: string;
  abbr: string;
}

export interface DayOfWeek {
  name: string;
  abbr: string;
}

export type DateFilter = (date: Date) => boolean;

export const CURRENT_DAY = new Date().getDate();
export const CURRENT_YEAR = new Date().getFullYear();
export const CURRENT_MONTH = new Date().getMonth();
export const MAX_YEARS_RANGE = 120;

const WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const TOTAL_WEEKS = 6;

export const generateCalendarMatrix = (
  year: string | number = CURRENT_YEAR,
  month: string | number = CURRENT_MONTH,
) => {
  const yearNumber = enforceNumber(year);
  const monthNumber = enforceNumber(month);
  let offset = new Date(yearNumber, monthNumber, 1).getDay();
  const lastDay = new Date(yearNumber, monthNumber + 1, 0).getDate();
  let dayCount = 1;
  const calendar: string[][] = [];

  for (let i = 0; i < TOTAL_WEEKS; i++) {
    const week: string[] = [];

    WEEK.forEach(() => {
      if (offset > 0) {
        week.push('');
        offset--;

        return;
      }

      if (dayCount > lastDay) {
        if (week.length <= 7) {
          week.push('');
          dayCount++;
        }

        return;
      }

      week.push(String(dayCount));
      dayCount++;
    });

    calendar.push(week);

    if (dayCount > lastDay) {
      break;
    }
  }

  return calendar;
};

/**
 * Lista de páises e seus respectivos formatos
 *
 * *#preencher*
 */
export const LOCALE_DATE_FORMATS: Record<Locale, string> = {
  'cs-CZ': 'dd/mm/yyyy',
  'da-DK': 'dd/mm/yyyy',
  'de-AT': 'dd/mm/yyyy',
  'de-CH': 'dd/mm/yyyy',
  'de-DE': 'dd/mm/yyyy',
  'el-GR': 'dd/mm/yyyy',
  'en-AU': 'dd/mm/yyyy',
  'en-CA': 'yyyy-mm-dd',
  'en-GB': 'dd/mm/yyyy',
  'en-IE': 'dd/mm/yyyy',
  'en-IN': 'dd/mm/yyyy',
  'en-NZ': 'dd/mm/yyyy',
  'en-US': 'mm/dd/yyyy',
  'en-ZA': 'yyyy/mm/dd',
  'es-AR': 'dd/mm/yyyy',
  'es-CL': 'dd/mm/yyyy',
  'es-CO': 'dd/mm/yyyy',
  'es-ES': 'dd/mm/yyyy',
  'es-MX': 'dd/mm/yyyy',
  'es-US': 'dd/mm/yyyy',
  'fi-FI': 'dd/mm/yyyy',
  'fr-BE': 'dd/mm/yyyy',
  'fr-CA': 'yyyy/mm/dd',
  'fr-CH': 'dd/mm/yyyy',
  'fr-FR': 'dd/mm/yyyy',
  'he-IL': 'dd/mm/yyyy',
  'hi-IN': 'dd/mm/yyyy',
  'hu-HU': 'yyyy/mm/dd',
  'id-ID': 'dd/mm/yyyy',
  'it-CH': 'dd/mm/yyyy',
  'it-IT': 'dd/mm/yyyy',
  'ja-JP': 'yyyy/mm/dd',
  'ko-KR': 'yyyy/mm/dd',
  'nl-BE': 'dd/mm/yyyy',
  'nl-NL': 'dd/mm/yyyy',
  'no-NO': 'dd/mm/yyyy',
  'pl-PL': 'dd/mm/yyyy',
  'pt-BR': 'dd/mm/yyyy',
  'pt-PT': 'dd/mm/yyyy',
  'ro-RO': 'dd/mm/yyyy',
  'ru-RU': 'dd/mm/yyyy',
  'sk-SK': 'dd/mm/yyyy',
  'sv-SE': 'yyyy/mm/dd',
  'ta-IN': 'dd/mm/yyyy',
  'ta-LK': 'dd/mm/yyyy',
  'th-TH': 'dd/mm/2566',
  'tr-TR': 'dd/mm/yyyy',
  'zh-CN': 'yyyy/mm/dd',
  'zh-HK': 'dd/mm/yyyy',
  'zh-TW': 'yyyy/mm/dd',
};

export const PT_BR_MONTHS: Month[] = [
  { id: 0, name: 'janeiro', abbr: 'jan' },
  { id: 1, name: 'fevereiro', abbr: 'fev' },
  { id: 2, name: 'março', abbr: 'mar' },
  { id: 3, name: 'abril', abbr: 'abr' },
  { id: 4, name: 'maio', abbr: 'mai' },
  { id: 5, name: 'junho', abbr: 'jun' },
  { id: 6, name: 'julho', abbr: 'jul' },
  { id: 7, name: 'agosto', abbr: 'ago' },
  { id: 8, name: 'setembro', abbr: 'set' },
  { id: 9, name: 'outubro', abbr: 'out' },
  { id: 10, name: 'novembro', abbr: 'nov' },
  { id: 11, name: 'dezembro', abbr: 'dez' },
];

export const PT_BR_DAYS_OF_WEEK: DayOfWeek[] = [
  { name: 'domingo', abbr: 'D' },
  { name: 'segunda-feira', abbr: 'S' },
  { name: 'terça-feira', abbr: 'T' },
  { name: 'quarta-feira', abbr: 'Q' },
  { name: 'quinta-feira', abbr: 'Q' },
  { name: 'sexta-feira', abbr: 'S' },
  { name: 'sábado', abbr: 'S' },
];

export const isUniversalDateFormat = (stringDate = '') => {
  const pattern = /^\d{4}\/\d{2}\/\d{2}$/;

  return pattern.test(stringDate);
};

/**
 * Formata a string de data para o padrão do input date (yyyy/mm/dd)
 *
 * @param date string de data
 * @param dateFormat formato de entrada data
 *
 * @returns string de data formatada
 *
 * @example
 * ```ts
 * import { convertToUniversalDate } from '@ids/tools';
 *
 * convertToUniversalDate('11/01/2021'); //Saída: 2021/01/11
 * convertToUniversalDate('01/11/2021', 'mm/dd/yyyy'); //Saída: 2021/01/11
 * ```
 */
export const convertToUniversalDate = (date: string, dateFormat = 'dd/mm/yyyy') => {
  if (!date || date.length !== dateFormat.length) {
    return date;
  }

  const initDay = dateFormat.indexOf('d') !== -1 ? dateFormat.indexOf('d') : 0;
  const initMonth = dateFormat.indexOf('m') !== -1 ? dateFormat.indexOf('m') : 3;
  const initYear = dateFormat.indexOf('y') !== -1 ? dateFormat.indexOf('y') : 6;

  const endDay = dateFormat.match(/d|D/g)?.length || 2;
  const endMonth = dateFormat.match(/m|M/g)?.length || 2;
  const endYear = dateFormat.match(/y|Y/g)?.length || 4;

  const day = date.slice(initDay, initDay + endDay);
  const month = date.slice(initMonth, initMonth + endMonth);
  const year = date.slice(initYear, initYear + endYear);

  return `${year}/${month}/${day}`;
};

export interface ValidateDateOptions {
  min?: string;
  max?: string;
  format?: string;
  filter?: DateFilter;
}

export const validateDate = (
  stringDate: string,
  { min, max, format = 'dd/mm/yyyy', filter }: ValidateDateOptions = {},
) => {
  // valida se a data está completamente escrita
  if (!stringDate || stringDate.length < format.length) {
    return false;
  }

  const universalStringDate = convertToUniversalDate(stringDate);
  const date = new Date(universalStringDate);

  // valida se o valor inserido é uma data válida
  if (isNaN(date.getTime())) {
    return false;
  }

  const [year, month, day] = universalStringDate.split('/');

  if (
    day !== ('0' + date.getDate()).slice(-2) ||
    month !== ('0' + (date.getMonth() + 1)).slice(-2) ||
    year !== String(date.getFullYear())
  ) {
    return false;
  }

  if (min) {
    const newDate = new Date(convertToUniversalDate(stringDate, format));
    const minDate = new Date(convertToUniversalDate(min, format));

    if (newDate.getTime() < minDate.getTime()) {
      return false;
    }
  }

  if (max) {
    const newDate = new Date(convertToUniversalDate(stringDate, format));
    const maxDate = new Date(convertToUniversalDate(max, format));

    if (newDate.getTime() > maxDate.getTime()) {
      return false;
    }
  }

  if (filter) {
    return filter(date);
  }

  return true;
};

/**
 * Converte a data para o formato de string de acordo com a linguagem configurada
 */
export const formatDateToString = (date: Date, { lang = 'pt-BR' } = {}) =>
  new Intl.DateTimeFormat(lang, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  }).format(date);

export const formatStringToDate = (stringDate: string) => {
  const [day, month, year] = stringDate.split('/').map(Number);

  return new Date(year, month - 1, day);
};

/**
 * Verifica se a data enviada é final de semana.
 */
export const verifyIsWeekend = (date: Date) => {
  const day = date.getDay();

  return day === 0 || day === 6;
};
